import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, Save, Loader2, X, Camera, Copy, Image as ImageIcon, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLES = ["all", "guest", "discovery", "community", "enhancer", "location_manager", "admin"];
const BLANK = { title: "", role: "all", category: "Guides", icon: "BookOpen", order: 0, summary: "", published: true, admin_notes: "", topics: [] };

export default function HelpPanel() {
  const { data: articles = [], refetch } = useQuery({
    queryKey: ["admin-help"], queryFn: () => api.get("/admin/help/articles").then((r) => r.data),
  });
  const [editing, setEditing] = useState(null);
  const [topicsText, setTopicsText] = useState("[]");
  const [saving, setSaving] = useState(false);
  const { data: keypages = [] } = useQuery({ queryKey: ["help-keypages"], queryFn: () => api.get("/admin/help/keypages").then((r) => r.data) });
  const { data: shots = [], refetch: refetchShots } = useQuery({ queryKey: ["help-shots"], queryFn: () => api.get("/admin/help/screenshots").then((r) => r.data) });
  const [capturing, setCapturing] = useState("");
  const [exporting, setExporting] = useState(false);

  const downloadManual = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/help/export.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "EYW-Operations-Manual.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Operations Manual downloaded");
    } catch (e) { toast.error("Could not generate the PDF. Please try again."); }
    finally { setExporting(false); }
  };

  const shotByKey = Object.fromEntries(shots.map((s) => [s.key, s]));

  const capture = async (key) => {
    setCapturing(key);
    try {
      await api.post("/admin/help/capture", { base_url: window.location.origin, key });
      toast.success(`Captured ${key}`);
      refetchShots();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCapturing(""); }
  };

  const captureAll = async () => {
    for (const p of keypages) { await capture(p.key); }
  };

  const copyUrl = (path) => {
    const full = window.location.origin + path;
    navigator.clipboard.writeText(full);
    toast.success("Screenshot URL copied — paste into a step's screenshot_url");
  };

  const open = (a) => {
    const art = a || BLANK;
    setEditing({ ...art });
    setTopicsText(JSON.stringify(art.topics || [], null, 2));
  };

  const save = async () => {
    let topics;
    try { topics = JSON.parse(topicsText); }
    catch { toast.error("Topics JSON is invalid"); return; }
    setSaving(true);
    try {
      const payload = { ...editing, topics };
      delete payload.id; delete payload._id; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.put(`/admin/help/articles/${editing.id}`, payload);
      else await api.post("/admin/help/articles", payload);
      toast.success("Guide saved"); setEditing(null); refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await api.delete(`/admin/help/articles/${a.id}`); toast.success("Deleted — your changes are live"); refetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-help-panel">
      {/* Screenshot capture */}
      <div className="eyw-card !p-6 mb-8" data-testid="help-screenshots-section">
        <div className="flex items-center justify-between mb-4">
          <div><h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Camera size={20} /> Screenshots</h2><p className="text-sm text-[#4A5D6B]">Auto-capture real screenshots of key pages, then copy a URL into any guide step's <code>screenshot_url</code> to replace its placeholder.</p></div>
          <button onClick={captureAll} disabled={!!capturing} data-testid="capture-all-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">{capturing ? <Loader2 className="animate-spin" size={15} /> : <Camera size={15} />} Capture all</button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {keypages.map((p) => {
            const shot = shotByKey[p.key];
            return (
              <div key={p.key} className="rounded-xl border border-[#E5E0D8] overflow-hidden bg-[#F9F7F3]" data-testid={`shot-${p.key}`}>
                <div className="aspect-video bg-[#F1EEE8] grid place-items-center overflow-hidden">
                  {shot ? <img src={shot.path} alt={p.label} className="w-full h-full object-contain" /> : <ImageIcon size={22} className="text-[#8FA89B]" />}
                </div>
                <div className="p-2.5">
                  <p className="text-xs font-medium text-[#1A242B] truncate">{p.label}</p>
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={() => capture(p.key)} disabled={capturing === p.key} data-testid={`capture-${p.key}`} className="text-xs text-[#144F75] hover:underline disabled:opacity-50">{capturing === p.key ? "Capturing…" : shot ? "Recapture" : "Capture"}</button>
                    {shot && <button onClick={() => copyUrl(shot.path)} data-testid={`copy-${p.key}`} className="text-xs text-[#4A5D6B] hover:underline inline-flex items-center gap-1"><Copy size={11} /> Copy URL</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div><h2 className="font-serif text-2xl text-[#1A242B]">Help Center guides</h2><p className="text-sm text-[#4A5D6B]">Edit how-to guides. Swap [SCREEN GRAB] placeholders for real screenshots via each step's <code>screenshot_url</code>.</p></div>
        <div className="flex gap-2 shrink-0">
          <button onClick={downloadManual} disabled={exporting} data-testid="help-export-pdf" className="eyw-btn-ghost !py-2 text-sm disabled:opacity-60">{exporting ? <Loader2 className="animate-spin" size={16} /> : <FileDown size={16} />} Operations Manual (PDF)</button>
          <button onClick={() => open(null)} data-testid="help-new-btn" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New guide</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {articles.map((a) => (
          <div key={a.id} className="eyw-card !p-5 flex items-start justify-between gap-3" data-testid={`help-row-${a.slug}`}>
            <div>
              <div className="flex items-center gap-2"><h3 className="font-medium text-[#1A242B]">{a.title}</h3>{!a.published && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Draft</span>}</div>
              <p className="text-xs text-[#8FA89B] mt-0.5">{a.category} · role: {a.role} · {(a.topics || []).length} topics</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => open(a)} data-testid={`help-edit-${a.slug}`} className="text-[#144F75] text-sm hover:underline">Edit</button>
              <button onClick={() => del(a)} className="text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="help-editor">
            <div className="flex items-center justify-between mb-5"><h3 className="font-serif text-2xl text-[#1A242B]">{editing.id ? "Edit guide" : "New guide"}</h3><button onClick={() => setEditing(null)}><X /></button></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="text-xs text-[#4A5D6B]">Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="help-f-title" className="eyw-input w-full text-sm" /></div>
              <div><label className="text-xs text-[#4A5D6B]">Role</label><select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="help-f-role" className="eyw-input w-full text-sm">{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
              <div><label className="text-xs text-[#4A5D6B]">Category</label><input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="eyw-input w-full text-sm" /></div>
              <div><label className="text-xs text-[#4A5D6B]">Icon (lucide name)</label><input value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="eyw-input w-full text-sm" /></div>
              <div><label className="text-xs text-[#4A5D6B]">Order</label><input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="eyw-input w-full text-sm" /></div>
              <div className="sm:col-span-2"><label className="text-xs text-[#4A5D6B]">Summary</label><textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} rows={2} className="eyw-input w-full text-sm" /></div>
              <div className="sm:col-span-2"><label className="text-xs text-[#4A5D6B]">Topics (JSON)</label><textarea value={topicsText} onChange={(e) => setTopicsText(e.target.value)} rows={10} data-testid="help-f-topics" className="eyw-input w-full text-xs font-mono" /></div>
              <label className="flex items-center gap-2 text-sm text-[#1A242B]"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#144F75]" /> Published</label>
            </div>
            <div className="flex justify-end mt-6"><button onClick={save} disabled={saving} data-testid="help-save-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save guide</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
